import React, { useMemo, useRef } from "react";
import { intlShape } from "@ai360/core/intl";
import PropTypes from "prop-types";

import { ReportAPI } from "@ai360/core";

import { DateInput, Section, SelectInput, SubSection } from "~/core";
import CroppingSeason from "~/reports/components/inputs/cropping-season";
import { selectors as picklistSelectors, picklistNames } from "~/core/picklist";
import { messages } from "~/reports/i18n-messages";
import { messages as globalMessages } from "~/i18n-messages";
import { model } from "~/reports/data";
import { getReportDataByReportType, getParsedCropData } from "~/reports/data/selectors";
import { useCropList } from "~/admin/setup/customer/use-crop-list";
import {
    IS_SELECTABLE,
    useLoggedInUserProductScopeOrgLevelGuid,
} from "~/admin/setup/customer/use-product-list";
import { getSelectedEventGuidSet } from "~/action-panel/components/event-module/components/event-list/selectors";

import { FieldAPI } from "@ai360/core";
import { useAppSelector } from "~/store/hooks";
import { IOption, ReportOptionsComponentProps } from ".";
import { getEventGeneralGuidToEventSummaryMap } from "~/recs-events/events/selectors";
import { getDisplayTypeOptions } from "../utils";
import { useIntl } from "react-intl";

type SkyHarvestSummaryProps = {
    selectedCrop: FieldAPI.ICrop | null;
};

function SkyHarvestSummary({
    hideDefaultOptions,
    onInputChange,
    selectedCrop,
}: ReportOptionsComponentProps & SkyHarvestSummaryProps) {
    const { formatMessage } = useIntl();
    const reportData = useAppSelector(getReportDataByReportType(SkyHarvestSummary.reportName));
    const cropScopeOrgLevelGuid = useLoggedInUserProductScopeOrgLevelGuid();
    const { crops: rawCrops } = useCropList({ orgLevelGuid: cropScopeOrgLevelGuid });
    const crops = useMemo(
        () =>
            getParsedCropData(
                rawCrops.map((crop) => ({
                    cropGuid: crop.cropGuid,
                    cropId: String(crop.cropId),
                    cropName: crop.name,
                    activeYn: crop.activeYn,
                    isAgvanceLinked: crop.isAgvanceLinked,
                    ...(crop.isAgvanceLinked === false && {
                        getToolTip: () => formatMessage(globalMessages.cropNotLinkedInAgvance),
                    }),
                }))
            ),
        [rawCrops]
    );
    const agEventSummaryMap = useAppSelector(getEventGeneralGuidToEventSummaryMap);
    const seasons = useAppSelector((state) =>
        picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(picklistNames.PICKLIST_CROPPING_SEASON)
        )
    );

    const selectedEventGuidList = useAppSelector(getSelectedEventGuidSet);
    const numberOfSeasons: IOption[] = [...Array(20)].map((e, i) => {
        return { label: (i + 1).toString(), value: (i + 1).toString() };
    });
    const displayTypeOptions: IOption[] = getDisplayTypeOptions(formatMessage);
    const seasonsToDisplay = useRef<string[]>();

    React.useEffect(() => {
        onInputChange(model.PROPS_SELECTED_EVENT_GUID_LIST, [...selectedEventGuidList]);

        if (hideDefaultOptions) {
            return;
        }
        if (selectedEventGuidList.size > 0) {
            const seasonGuids = [];

            for (const eventGeneralGuid of selectedEventGuidList) {
                const event = agEventSummaryMap.get(eventGeneralGuid);
                if (event.agEventTypeName === "Harvest") {
                    seasonGuids.push(event.croppingSeasonGuid);
                }
            }
            const seasonsText = [];
            for (const seasonGuid of seasonGuids) {
                const selectedSeason = seasons.find((season) => season.value === seasonGuid);
                if (seasonsText.indexOf(selectedSeason.label) === -1) {
                    seasonsText.push(selectedSeason.label);
                }
            }
            seasonGuids.sort();
            seasonsToDisplay.current = seasonsText;
        } else {
            seasonsToDisplay.current = [];
        }
    }, [selectedEventGuidList.size]);

    const optionsDisabled = selectedEventGuidList.size > 0;
    const croppingSeasonDisabled =
        reportData[model.PROPS_START_DATE] ||
        reportData[model.PROPS_END_DATE] ||
        selectedEventGuidList.size > 0;

    return (
        <Section className="report-options" testId="sky-harvest-summary-options-section">
            {!hideDefaultOptions ? (
                <React.Fragment>
                    <SubSection testId="sky-harvest-summary-season-and-crop-subsection">
                        <CroppingSeason
                            disabled={croppingSeasonDisabled}
                            required={!croppingSeasonDisabled}
                            reportType={SkyHarvestSummary.reportName}
                            valuesToDisplay={seasonsToDisplay.current}
                        />
                        <SelectInput<FieldAPI.ICrop>
                            disabled={optionsDisabled}
                            optionIsHiddenKey={IS_SELECTABLE}
                            placeholderText={formatMessage(messages.crop)}
                            options={optionsDisabled ? [] : crops}
                            onChange={(value) => {
                                onInputChange(model.PROPS_CROP_GUID, value ? value.cropGuid : null);
                            }}
                            value={optionsDisabled ? null : (selectedCrop as FieldAPI.ICrop)}
                            testId="sky-harvest-summary-crop-select"
                        />
                    </SubSection>
                    <SubSection testId="sky-harvest-summary-dates-subsection">
                        <DateInput
                            disabled={optionsDisabled}
                            placeholderText={formatMessage(messages.startDate)}
                            onChange={(value) =>
                                onInputChange(model.PROPS_START_DATE, value ? value : null)
                            }
                            timeFormat={false}
                            value={optionsDisabled ? null : reportData[model.PROPS_START_DATE]}
                            testId="sky-harvest-summary-start-date-date"
                        />
                        <DateInput
                            disabled={optionsDisabled}
                            placeholderText={formatMessage(messages.endDate)}
                            onChange={(value) =>
                                onInputChange(model.PROPS_END_DATE, value ? value : null)
                            }
                            timeFormat={false}
                            value={optionsDisabled ? null : reportData[model.PROPS_END_DATE]}
                            testId="sky-harvest-summary-end-date-date"
                        />
                    </SubSection>
                </React.Fragment>
            ) : null}
            <SubSection
                className="two-report-options"
                testId="sky-harvest-summary-number-of-seasons-subsection"
            >
                <SelectInput
                    clearable={false}
                    disabled={!hideDefaultOptions && croppingSeasonDisabled}
                    containerClassNames={["display-options-list"]}
                    placeholderText={formatMessage(messages.numberOfSeasons)}
                    options={numberOfSeasons}
                    onChange={(value) => {
                        onInputChange(model.PROPS_NUMBER_OF_SEASONS, value);
                    }}
                    value={reportData[model.PROPS_NUMBER_OF_SEASONS]}
                    testId="sky-harvest-summary-number-of-seasons-select"
                />
                <SelectInput
                    required
                    clearable={false}
                    containerClassNames={["display-options-list"]}
                    placeholderText={formatMessage(messages.displayType)}
                    options={displayTypeOptions}
                    onChange={(value) => {
                        onInputChange(model.PROPS_DISPLAY_TYPE, value);
                    }}
                    value={reportData[model.PROPS_DISPLAY_TYPE]}
                    testId="sky-harvest-summary-display-type-select"
                />
            </SubSection>
        </Section>
    );
}

SkyHarvestSummary.propTypes = {
    intl: intlShape,
    reportPreferences: PropTypes.object,
    selectedCrop: PropTypes.object,
};

SkyHarvestSummary.reportPreferences = [model.PROPS_DISPLAY_TYPE];

SkyHarvestSummary.defaultReportOptions = {
    [model.PROPS_CROP_GUID]: null,
    [model.PROPS_SELECTED_EVENT_GUID_LIST]: [],
    [model.PROPS_DISPLAY_TYPE]: "coverage",
    [model.PROPS_START_DATE]: null,
    [model.PROPS_END_DATE]: null,
    [model.PROPS_CROPPING_SEASON_GUID]: [],
    [model.PROPS_NUMBER_OF_SEASONS]: "3",
};

SkyHarvestSummary.reportName = ReportAPI.ReportNames.AGVANCE_SKY_HARVEST_SUMMARY;

export default SkyHarvestSummary;
